/**
 * Settings → History (QB-083, QA-36): who changed the business profile, business details, WhatsApp and
 * notification settings, when, and what each value was before and after. Needs `audit:read` (Owner and
 * Business Administrator); restoring a version is a normal save, so it also needs `organization:update`.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { History as HistoryIcon, RotateCcw } from "lucide-react";
import { useState } from "react";

import { useOrganization } from "@/auth/organization";
import { useUser } from "@/auth/session";
import { shortName } from "@/components/team/roles";
import {
  Button,
  Section,
  EmptyState,
  ErrorAlert,
  ErrorState,
  FullPageSpinner,
  Modal,
  Select,
  useToast,
} from "@/components/ui";
import { identity, portal } from "@/lib/api";
import { formatDateTime, timeAgo } from "@/lib/format";
import type { SettingsArea, SettingsChange, SettingsVersion } from "@/lib/types";

import { CURRENCIES, INDUSTRIES, TIME_ZONES } from "./options";
import { PanelHead } from "./parts";

export const AREA_LABEL: Record<SettingsArea, string> = {
  profile: "Business profile",
  details: "Business details",
  whatsapp: "WhatsApp",
  notifications: "Notifications",
};

/** "Sam P. changed …" */
const AREA_PHRASE: Record<SettingsArea, string> = {
  profile: "the business profile",
  details: "the business details",
  whatsapp: "the WhatsApp settings",
  notifications: "their notification preferences",
};
/** "… go back to the values saved …" (only restorable areas) */
const AREA_SUBJECT: Record<SettingsArea, string> = {
  profile: "The business profile settings",
  details: "The business details",
  whatsapp: "The WhatsApp settings",
  notifications: "Your notification preferences",
};

const FIELD_LABEL: Record<string, string> = {
  time_zone: "Time zone",
  industry: "Industry",
  currency: "Currency",
  offering: "What you offer",
  contact_email: "Contact e-mail",
  country: "Country",
  dial_code: "Country code",
  number: "WhatsApp number",
  message: "Prefilled message",
  button_enabled: "Website chat button",
  button_position: "Button position",
  muted_categories: "Muted in the inbox",
  digest_frequency: "Digest frequency",
  "email_updates.digest": "E-mail: Performance digest",
  "email_updates.domains_hosting": "E-mail: Domains & hosting",
  "email_updates.email": "E-mail: Email",
  "email_updates.team": "E-mail: Team & access",
  "email_updates.website": "E-mail: Website",
  "email_updates.weavex_suggestions": "E-mail: Weavex suggestions",
  "email_updates.growth_intelligence": "E-mail: Growth Intelligence",
};

const NAMED: Record<string, [string, string][]> = {
  time_zone: TIME_ZONES,
  industry: INDUSTRIES,
  currency: CURRENCIES,
};

const humanize = (s: string) => (s.charAt(0).toUpperCase() + s.slice(1)).replaceAll("_", " ");

export function fieldLabel(field: string): string {
  return FIELD_LABEL[field] ?? humanize(field.split(".").at(-1) ?? field);
}

/** One recorded value as people read it on the Settings page. */
export function showValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "On" : "Off";
  if (Array.isArray(value)) return value.length ? value.map((v) => humanize(String(v))).join(", ") : "None";
  const named = NAMED[field]?.find(([key]) => key === value)?.[1];
  if (named) return named;
  if (field === "button_position" || field === "digest_frequency") return humanize(String(value));
  return String(value);
}

export function HistoryTab() {
  const me = useUser();
  const qc = useQueryClient();
  const toast = useToast();
  const { orgId } = useOrganization();
  const [area, setArea] = useState<SettingsArea | "">("");
  const [restoring, setRestoring] = useState<SettingsVersion | null>(null);

  const members = useQuery({ queryKey: ["members", orgId], queryFn: () => identity.members(orgId) });
  const history = useInfiniteQuery({
    queryKey: ["settings-history", orgId, area],
    queryFn: ({ pageParam }) =>
      portal.settingsHistory(orgId, { area: area || undefined, page_token: pageParam || undefined }),
    initialPageParam: "",
    getNextPageParam: (last) => last.next_page_token ?? undefined,
  });
  const restore = useMutation({
    mutationFn: (v: SettingsVersion) => portal.restoreSettingsVersion(orgId, v.id),
    onSuccess: async (res) => {
      setRestoring(null);
      toast.show({ tone: "success", title: `${AREA_LABEL[res.area]} restored` });
      await Promise.all(
        ["settings-history", "business-profile", "whatsapp"].map((k) =>
          qc.invalidateQueries({ queryKey: [k, orgId] }),
        ),
      );
    },
  });

  const byId = new Map((members.data?.items ?? []).map((m) => [m.user.id, m.user]));
  const nameOf = (v: SettingsVersion) => {
    if (!v.actor_user_id) return v.actor_kind === "staff" ? "CortexGrip support" : "CortexGrip";
    if (v.actor_user_id === me.id) return "You";
    const u = byId.get(v.actor_user_id);
    return u ? shortName(u.display_name, u.email) : "A former member";
  };

  if (history.isPending) return <FullPageSpinner />;
  if (history.isError) return <ErrorState error={history.error} onRetry={() => void history.refetch()} />;
  const versions = history.data.pages.flatMap((p) => p.items);

  return (
    <Section>
      <PanelHead icon={HistoryIcon} title="Change history">
        <Select
          aria-label="Which settings"
          value={area}
          onChange={(e) => setArea(e.target.value as SettingsArea | "")}
          // `!` beats the full-width, rounded-lg control defaults (cx doesn't merge classes), so the
          // filter keeps its own width and the heading isn't squeezed.
          className="h-8 w-auto! shrink-0 rounded-full! py-1! text-[13px]!"
        >
          <option value="">All settings</option>
          {(Object.keys(AREA_LABEL) as SettingsArea[]).map((a) => (
            <option key={a} value={a}>
              {AREA_LABEL[a]}
            </option>
          ))}
        </Select>
      </PanelHead>
      <p className="text-sm text-fg-subtle">
        Every saved change to these settings, newest first. Entries can't be edited or deleted.
      </p>

      {versions.length === 0 ? (
        <EmptyState
          icon={<HistoryIcon className="size-5" aria-hidden />}
          title="No changes yet"
          description="Changes appear here as soon as someone saves these settings."
        />
      ) : (
        <ul className="divide-y divide-border border-t border-border">
          {versions.map((v) => (
            <li key={v.id} className="flex flex-col gap-2 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-fg">
                  {nameOf(v)} changed {AREA_PHRASE[v.area]}
                </span>
                <time
                  className="text-xs text-fg-subtle"
                  dateTime={v.occurred_at}
                  title={formatDateTime(v.occurred_at)}
                >
                  {timeAgo(v.occurred_at)}
                </time>
                {v.restorable && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto"
                    requires="organization:update"
                    onClick={() => {
                      restore.reset();
                      setRestoring(v);
                    }}
                  >
                    <RotateCcw className="size-3.5" aria-hidden /> Restore this version
                  </Button>
                )}
              </div>
              <ChangeTable changes={v.changes} known={v.values_known} />
            </li>
          ))}
        </ul>
      )}

      {history.hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="secondary"
            size="sm"
            className="rounded-full"
            loading={history.isFetchingNextPage}
            onClick={() => void history.fetchNextPage()}
          >
            Show older
          </Button>
        </div>
      )}

      <Modal
        open={!!restoring}
        onClose={() => setRestoring(null)}
        title="Restore this version?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRestoring(null)}>
              Cancel
            </Button>
            <Button loading={restore.isPending} onClick={() => restoring && restore.mutate(restoring)}>
              Restore
            </Button>
          </>
        }
      >
        {restoring && (
          <div className="flex flex-col gap-3 text-sm text-fg-muted">
            <p>
              {AREA_SUBJECT[restoring.area]} go back to the values saved{" "}
              {formatDateTime(restoring.occurred_at)}. This is saved as a new change, so it can be undone the
              same way.
            </p>
            {restore.error && <ErrorAlert error={restore.error} />}
          </div>
        )}
      </Modal>
    </Section>
  );
}

function ChangeTable({ changes, known }: { changes: SettingsChange[]; known: boolean }) {
  if (!known) {
    return (
      <p className="text-xs text-fg-muted">
        Changed {changes.map((c) => fieldLabel(c.field).toLowerCase()).join(", ") || "settings"}. Values
        weren't kept for changes made before history was added.
      </p>
    );
  }
  return (
    <table className="w-full text-left text-[13px]">
      <thead className="sr-only">
        <tr>
          <th scope="col">Setting</th>
          <th scope="col">Before</th>
          <th scope="col">After</th>
        </tr>
      </thead>
      <tbody>
        {changes.map((c) => (
          <tr key={c.field} className="align-top">
            <th scope="row" className="w-1/3 py-0.5 pr-3 font-medium text-fg-muted">
              {fieldLabel(c.field)}
            </th>
            <td className="py-0.5 pr-2 text-fg-subtle line-through decoration-fg-subtle/50">
              {showValue(c.field, c.before)}
            </td>
            <td className="py-0.5 text-fg">
              <span aria-hidden className="mr-2 text-fg-subtle">
                →
              </span>
              {showValue(c.field, c.after)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
